import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@me8eon/translation";
import { EonUiIcon, EonUiPreloaderCircle } from "@eon-ui/eon-ui-components-react";
import { PromptAreaBase } from "../../../../features/AiPrompt/organisms/PromptAreaBase";
import { PromptAreaBaseProps } from "../../../../features/AiPrompt/model/types";
import useSearchStore, { newAiQue } from "../../../../store/searchStore";
import { useAiAssistantPromptArea } from "../../hooks/useAiAssistantPromptArea";
import { SimpleOutsideClick } from "@me8eon/outside_click";
import { ListItem } from "../../../../shared/ui/molecules/ListItem";
import "./AiAssistantPromptArea.scss";
import { useLoadingFlag } from "@me8eon/async";
import { useDoAi } from "@me8eon/react_intent";
import GeminiAssistantVoice, { GeminiAssistantVoiceRef } from "../../GeminiAssistantVoice";
import { Icon } from "@me8eon/icons";

type Props = {
    id?: string;
    value: string;
    onChange: (v: string) => void;
    onSubmit: (v: string) => void;
    hasListBelow?: boolean;
    imageSuggestions: string[];
    isTilePage?: boolean;
    reverseLayout?: boolean;
    listReverse?: boolean;
} & PromptAreaBaseProps

export function AiAssistantPromptArea({
    value,
    onChange,
    onSubmit,
    plugins,
    isTilePage,
    listReverse,
    imageSuggestions
}: Props) {
    const t = useTranslation();
    const [isFocused, setIsFocused] = useState(false);
    const [hideList, setHideList] = useState(false);
    const { loading, questionairData, clearQuestionData, setQuestionData } = useSearchStore();
    const { topOffset, listRef, onChangeTrigger } = useAiAssistantPromptArea(listReverse);
    const voiceRef = useRef<GeminiAssistantVoiceRef>(null);
    const [isVoiceListening, setIsVoiceListening] = useState(false);
    const handleAiQuery = useDoAi();
    const [isLoading] = useLoadingFlag();
    const isAiVocalPage = window.location.pathname === "/vocal-ai"

    useEffect(() => {
        setIsFocused(true);
        setHideList(false);
        setQuestionData(imageSuggestions.map((q) => ({ possible_user_question: q })) as newAiQue[]);
    }, [imageSuggestions]);

    const handleChange = (txt: string) => {
        onChange(txt);
        onChangeTrigger(txt);
    };

    return (
        <SimpleOutsideClick onClickOutside={() => {
            setHideList(true);
            setIsFocused(false);
        }}>
            {isAiVocalPage && (<><GeminiAssistantVoice ref={voiceRef} />
                <div style={{ margin: "0 auto", textAlign: "center" }}>
                    <button
                        style={{ height: "32px", width: "32px", borderRadius: "18px", border: `1px solid var(--primary-color)` }}
                        onClick={() => {
                            if (!voiceRef.current) return;

                            if (isVoiceListening) {
                                voiceRef.current.stopListening();
                                setIsVoiceListening(false);
                            } else {
                                voiceRef.current.startListening();
                                setIsVoiceListening(true);
                            }
                        }}
                    >
                        <Icon name={isVoiceListening ? "pause" : "microphone"} />
                    </button>
                </div>
            </>)}
            <div className="prompt-area-wrapper"  id="tileTour7">
                <PromptAreaBase
                    value={value}
                    onChange={handleChange}
                    onSubmit={onSubmit}
                    placeholder={t("chatBot.searchAsk") || "Ask E.ON"}
                    hasList={questionairData.length > 0 && isFocused}
                    listReverse={listReverse}
                    isFocused={isFocused}
                    onFocus={() => {
                        setIsFocused(true);
                        setHideList(false);
                    }}
                    plugins={plugins}
                    renderSendButton={({ disabled, onClick }) => (
                        <button className="prompt-area__send" onClick={onClick} disabled={disabled}>
                            <EonUiIcon
                                title={t("chatBot.sendbtn")}
                                size="small"
                                name="send"
                                style={{ color: disabled ? "#cccbca" : "#ea1b0a" }}
                            />
                        </button>
                    )}
                    className="input-wrapper"
                />
                {loading && (
                    <div className="spinner-loader">
                        <EonUiPreloaderCircle size="small" />
                    </div>
                )}
                {!hideList && questionairData.length > 0 && (
                    <ul ref={listRef} className={`list-item-ul ${listReverse ? "list-item-ul--reverse" : ""}`} style={{ top: -topOffset }}>
                        {questionairData.map(
                            (
                                result,
                                index: number,
                            ) => {
                                const title = 'possible_user_question' in result
                                    ? result.possible_user_question
                                    : 'title' in result
                                        ? result.title
                                        : '';
                                return (
                                    <ListItem
                                        resultData={{ title }}
                                        key={`${index + 1}`}
                                        isDisabled={isLoading}
                                        onClick={(question) => {
                                            if (isTilePage) {
                                                onChange(question);
                                                onSubmit(question);
                                            } else {
                                                onChange("");
                                                handleAiQuery(question);
                                            }
                                            clearQuestionData();
                                        }
                                        }
                                    />
                                );
                            },
                        )}
                    </ul>
                )}
            </div>
        </SimpleOutsideClick>
    );
}